import React, { useState } from 'react'
import { useSelector } from 'react-redux'
import {
  Checkbox,
  FormControlLabel,
  TextField,
  Typography,
} from '@mui/material'
import InputLabel from '@mui/material/InputLabel'
import Select from '@mui/material/Select'
import MenuItem from '@mui/material/MenuItem'
import { toast } from 'react-toastify'
import { cloneDeep } from 'lodash'
import PropTypes from 'prop-types'

import classNames from 'classnames'
import { reportError } from 'utils/errorReporter'
import {
  USER_ROLE_LABELS,
  USER_ROLE_VALUES,
  USER_ROLES_DESCRIPTIONS,
} from '../../../utils/constants/kypStatuses'
import styles from './styles'
import ScopeManagement from '../ScopeManagement'
import { createInternalUser, updateInternalUser } from '../../../services/apiKYP'
import { Button, DialogConfirm } from '../../index'
import { ValidateEmail } from '../../../utils/validators'
import history from '../../../utils/history'
import { ROUTE_CONSTANTS } from '../../../utils/constants/routeConstants'

const initialValues = {
  email: '',
  display_name: '',
  validated_at: '',
  disabled: '',
  user_role: USER_ROLE_VALUES.ANALYST,
  scopes: [],
}

const UserEditor = ({ user }) => {
  const token = useSelector((state) => state.auth.access_token)

  const isNewUser = user === null

  const [userState, setUserState] = useState(isNewUser ? initialValues : user)
  const [isLoading, setIsLoading] = useState(false)
  const [openConfirmCreateUser, setOpenConfirmCreateUser] = useState(false)
  const classes = styles()

  const handleRoleChange = (event) => {
    const newUserRole = event.target.value
    const updatedUser = cloneDeep(userState)
    updatedUser.user_role = newUserRole
    setUserState(updatedUser)
  }

  const handleActiveChange = (event) => {
    const isDisabled = !event.target.checked
    setUserState((prevUser) => ({
      ...prevUser,
      disabled: isDisabled,
    }))
  }

  const validate = (requestBody) => {
    const { email, display_name, user_role } = requestBody

    if (!email) {
      toast.error('O e-mail é obrigatório.')
      return false
    }

    if (!ValidateEmail(email)) {
      toast.error('E-mail inválido.')
      return false
    }

    if (!display_name) {
      toast.error('O nome de usuário é obrigatório.')
      return false
    }

    if (!user_role) {
      toast.error('O perfil é obrigatório.')
      return false
    }

    return true
  }

  const redirectToUserList = () => {
    history.push(ROUTE_CONSTANTS.USER_MANAGEMENT)
    history.go()
  }

  const requestUserUpdate = () => {
    const requestBody = {
      email: userState.email,
      disabled: userState.disabled,
      user_role: userState.user_role,
    }

    const genericErrorMsg = 'Falha ao atualizar o usuário. Tente novamente.'

    setIsLoading(true)

    updateInternalUser(token, requestBody)
      .then((response) => {
        if (response.status === 204) {
          toast.success('Alterações salvas com sucesso!', { position: 'top-center' })
        } else {
          toast.error(genericErrorMsg)
        }
      })
      .catch((error) => {
        reportError(error)
        toast.error(genericErrorMsg)
      })
      .finally(() => {
        setIsLoading(false)
      })
  }

  const requestUserCreation = () => {
    const requestBody = {
      email: userState.email,
      user_role: userState.user_role,
      display_name: userState.display_name,
    }

    setIsLoading(true)

    const genericErrorMsg = 'Falha ao criar usuário. Tente novamente.'

    createInternalUser(token, requestBody)
      .then((response) => {
        if (response.status === 201) {
          toast.success('Usuário criado com sucesso!')
          redirectToUserList()
        } else {
          toast.error(genericErrorMsg)
        }
      })
      .catch((e) => {
        toast.error(e.response.data.detail || genericErrorMsg)
      })
      .finally(() => {
        setIsLoading(false)
      })
  }

  const handleClick = () => {
    const requestBody = {
      email: userState.email,
      user_role: userState.user_role,
      display_name: userState.display_name,
    }
    if (!validate(requestBody)) return

    if (isNewUser) {
      setOpenConfirmCreateUser(true)
    } else {
      requestUserUpdate()
    }
  }
  const handleFieldChange = (event) => {
    const key = event.target.id
    const { value } = event.target
    const newUserData = cloneDeep(userState)
    newUserData[key] = value
    setUserState(newUserData)
  }

  const onConfirmCreateUser = () => {
    requestUserCreation()
    setOpenConfirmCreateUser(false)
  }

  return (
    <div className={classes.root}>
      <div className={classes.topBar}>
        <h2>{isNewUser ? 'Criar usuário' : 'Editar usuário'}</h2>
        <div className={classes.actionButtonsRow}>
          <Button className={classes.cancelButton} onClick={() => history.goBack()}>
            Voltar
          </Button>

          <Button onClick={handleClick} loading={isLoading}>
            {isNewUser ? 'Criar' : 'Salvar'}
          </Button>
        </div>
      </div>
      <div className={classes.container}>
        <h3>Dados básicos</h3>
        <div className={classes.row}>
          <TextField
            id="display_name"
            className={classes.formContainer}
            variant="standard"
            label="Nome de exibição"
            type="text"
            required
            value={userState?.display_name}
            onChange={handleFieldChange}
            disabled={!isNewUser}
          />

          <TextField
            id="email"
            className={classes.formContainer}
            variant="standard"
            label="E-mail"
            type="email"
            required
            value={userState?.email}
            onChange={handleFieldChange}
            disabled={!isNewUser}
          />
        </div>
        {!isNewUser && (
          <div className={classes.row}>
            <div className={classNames(classes.formContainer, classes.checkContainer)}>
              <FormControlLabel
                control={<Checkbox />}
                label="Usuário validado"
                checked={userState?.validated_at !== null && userState?.validated_at !== undefined}
                disabled
              />
              <FormControlLabel
                control={<Checkbox checked={!userState?.disabled} onChange={handleActiveChange} />}
                label="Usuário ativo"
              />
            </div>
          </div>
        )}
        <div className={classes.row}>
          <div className={classes.formContainer}>
            <InputLabel className={classes.inputLabel} id="userRoleLabel">
              Cargo *
            </InputLabel>
            <Select
              style={{ marginTop: '6px', width: '100%' }}
              labelId="userRoleLabel"
              id="userRoleSelect"
              name="userRole"
              variant="standard"
              required
              value={userState?.user_role}
              onChange={handleRoleChange}
            >
              {Object.keys(USER_ROLE_VALUES).map((key) => (
                <MenuItem key={key} style={{ fontSize: '1.25rem' }} value={USER_ROLE_VALUES[key]}>
                  {USER_ROLE_LABELS[key]}
                </MenuItem>
              ))}
            </Select>
          </div>
        </div>
        <Typography variant="subtitle1">{USER_ROLES_DESCRIPTIONS[userState.user_role]}</Typography>
      </div>
      
      {userState?.user_role && <ScopeManagement selectedUserRole={userState.user_role} />}

      <DialogConfirm
        open={openConfirmCreateUser}
        onConfirm={onConfirmCreateUser}
        onCancel={() => setOpenConfirmCreateUser(false)}
        title={`Criar usuário com cargo ${USER_ROLE_LABELS[userState.user_role.toUpperCase()]}?`}
        contextText="Um link será encaminhado para o endereço de e-mail fornecido, permitindo a criação da senha."
      ></DialogConfirm>
    </div>
  )
}

UserEditor.propTypes = {
  user: PropTypes.shape({
    email: PropTypes.string,
    display_name: PropTypes.string,
    validated_at: PropTypes.string,
    disabled: PropTypes.bool,
    user_role: PropTypes.string,
    scopes: PropTypes.array,
  }),
}

UserEditor.defaultProps = {
  user: null,
}

export default UserEditor
